/**
 * @ipc/admin-ui · shared/api/pagination — модели пагинации backend.
 * НЕ абстрагированы в единый хук (контракты различаются): это утилиты-билдеры
 * searchParams и разбора ответа для каждой модели отдельно.
 *
 *  1) page-based  (payments/links/users/roles/api-keys/merchants/logs/audit/webhooks):
 *     ?page&limit → data[] + meta.pagination { page, limit, total, totalPages }
 *  2) cursor-based (analytics, *-export): ?cursor&limit → nextCursor / X-Next-Cursor
 */
import { asArray } from './guards';
import type { ResponseMeta } from './api-client';

// ── 1. page-based ────────────────────────────────────────────
export interface PageParams {
  page: number;
  limit: number;
}

export interface PageInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export function pageSearchParams(p: PageParams): Record<string, number> {
  return { page: p.page, limit: p.limit };
}

/**
 * Разбор page-based ответа: data (массив) + meta.pagination.
 * Fallback pagination, если meta отсутствует (укороченный envelope).
 */
export function parsePaginatedResult<T>(
  data: unknown,
  meta: ResponseMeta | null | undefined,
  toItem: (raw: unknown) => T | null,
  fallback: PageParams,
): { items: T[]; pagination: PageInfo } {
  const items: T[] = [];
  for (const raw of asArray(data)) {
    const mapped = toItem(raw);
    if (mapped !== null) {
      items.push(mapped);
    }
  }
  const pagination: PageInfo = meta?.pagination ?? {
    page: fallback.page,
    limit: fallback.limit,
    total: items.length,
    totalPages: 1,
  };
  return { items, pagination };
}

// ── 2. cursor-based (analytics, exports) ─────────────────────
export interface CursorParams {
  cursor?: number | null;
  limit: number;
}

export function cursorSearchParams(p: CursorParams): Record<string, number | undefined> {
  return { cursor: p.cursor ?? undefined, limit: p.limit };
}

/** Заголовок X-Next-Cursor → number | null (для экспортов, где курсор в заголовке). */
export function nextCursorFromHeader(headers: Headers): number | null {
  const raw = headers.get('X-Next-Cursor');
  if (raw === null || raw === '') {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}
